import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Tag,
  Progress,
  Typography,
  Space,
  Popconfirm,
  message,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface CaptioningTask {
  id: number;
  name: string;
  dataset_id: number;
  dataset_name: string;
  prompt_name: string;
  model: string;
  provider: string;
  status: string;
  total_images: number;
  pending_count: number;
  success_count: number;
  failed_count: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const CaptioningTaskList: React.FC = () => {
  const [tasks, setTasks] = useState<CaptioningTask[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const subscribedTasksRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    fetchTasks();

    // Fallback polling every 10 seconds (WebSocket handles real-time updates)
    intervalRef.current = setInterval(() => {
      if (tasks.some(t => t.status === 'running')) {
        fetchTasks();
      }
    }, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Re-setup interval when tasks change
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (tasks.some(t => t.status === 'running')) {
      intervalRef.current = setInterval(fetchTasks, 10000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [tasks]);

  // WebSocket connection for real-time updates on running tasks
  useEffect(() => {
    const runningTaskIds = tasks.filter(t => t.status === 'running').map(t => t.id);

    // If no running tasks, close WebSocket if open
    if (runningTaskIds.length === 0) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] No running tasks, closing connection');
        wsRef.current.close(1000, 'No running tasks');
        wsRef.current = null;
        subscribedTasksRef.current.clear();
      }
      return;
    }

    // Create WebSocket connection if not exists
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${process.env.REACT_APP_WS_HOST || 'localhost:3003'}/ws`;
      console.log('[WebSocket] Connecting for task list:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected for task list');
        // Subscribe to all running tasks
        runningTaskIds.forEach(taskId => {
          const subscribeMsg = { action: 'subscribe', channel: `captioning-task:${taskId}` };
          console.log('[WebSocket] Subscribing to task:', taskId);
          ws.send(JSON.stringify(subscribeMsg));
          subscribedTasksRef.current.add(taskId);
        });
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          const { data, type } = parsed;

          if (type === 'connected') return;

          // Progress update
          if (data?.type === 'caption-progress') {
            console.log('[WebSocket] Task list progress update:', data);
            setTasks(prev => prev.map(task =>
              task.id === data.taskId
                ? {
                    ...task,
                    success_count: data.successCount,
                    failed_count: data.failedCount,
                    pending_count: data.pendingCount
                  }
                : task
            ));
          }

          // Status change (task completed, failed, etc.)
          if (data?.type === 'task-status') {
            console.log('[WebSocket] Task status change:', data);
            setTasks(prev => prev.map(task =>
              task.id === data.taskId
                ? { ...task, status: data.status }
                : task
            ));
          }
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('[WebSocket] Task list error:', err);
      };

      ws.onclose = () => {
        console.log('[WebSocket] Task list disconnected');
        wsRef.current = null;
        subscribedTasksRef.current.clear();
      };
    } else {
      // WebSocket exists, manage subscriptions
      const currentSubscriptions = subscribedTasksRef.current;

      // Subscribe to new running tasks
      runningTaskIds.forEach(taskId => {
        if (!currentSubscriptions.has(taskId)) {
          const subscribeMsg = { action: 'subscribe', channel: `captioning-task:${taskId}` };
          console.log('[WebSocket] Subscribing to new task:', taskId);
          wsRef.current?.send(JSON.stringify(subscribeMsg));
          currentSubscriptions.add(taskId);
        }
      });

      // Unsubscribe from tasks that are no longer running
      currentSubscriptions.forEach(taskId => {
        if (!runningTaskIds.includes(taskId)) {
          const unsubscribeMsg = { action: 'unsubscribe', channel: `captioning-task:${taskId}` };
          console.log('[WebSocket] Unsubscribing from task:', taskId);
          wsRef.current?.send(JSON.stringify(unsubscribeMsg));
          currentSubscriptions.delete(taskId);
        }
      });
    }

    return () => {
      // Cleanup on unmount
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [tasks.filter(t => t.status === 'running').map(t => t.id).join(',')]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/captioning-tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch captioning tasks:', error);
      message.error('Failed to load captioning tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async (id: number) => {
    try {
      await axios.post(`${API_URL}/captioning-tasks/${id}/start`);
      message.success('Task started');
      fetchTasks();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to start task');
    }
  };

  const handlePause = async (id: number) => {
    console.log('[Pause] Attempting to pause task:', id);
    try {
      const response = await axios.post(`${API_URL}/captioning-tasks/${id}/pause`);
      console.log('[Pause] Response:', response.data);
      message.success('Task paused');
      fetchTasks();
    } catch (error: any) {
      console.error('[Pause] Error:', error);
      message.error(error.response?.data?.error || 'Failed to pause task');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/captioning-tasks/${id}`);
      setTasks(tasks.filter(t => t.id !== id));
      message.success('Task deleted');
    } catch (error) {
      message.error('Failed to delete task');
    }
  };

  const getStatusTag = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: 'default',
      running: 'processing',
      paused: 'warning',
      completed: 'success',
      failed: 'error',
    };
    return <Tag color={colors[status] || 'default'}>{status.toUpperCase()}</Tag>;
  };

  const getProgress = (task: CaptioningTask) => {
    if (task.total_images === 0) return 0;
    return Math.round(((task.success_count + task.failed_count) / task.total_images) * 100);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: CaptioningTask) => (
        <Button type="link" onClick={() => navigate(`/captioning/${record.id}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Dataset',
      dataIndex: 'dataset_name',
      key: 'dataset_name',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (model: string, record: CaptioningTask) => (
        <Tag color="blue">{model}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 250,
      render: (_: any, record: CaptioningTask) => (
        <div>
          <Progress
            percent={getProgress(record)}
            size="small"
            status={record.status === 'failed' ? 'exception' : record.status === 'running' ? 'active' : undefined}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <span style={{ color: '#52c41a' }}>{record.success_count}</span>
            {' / '}
            <span style={{ color: '#ff4d4f' }}>{record.failed_count}</span>
            {' / '}
            {record.total_images}
          </Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CaptioningTask) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/captioning/${record.id}`)}
          />
          {(record.status === 'pending' || record.status === 'paused') && (
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStart(record.id)}
              style={{ color: '#52c41a' }}
            />
          )}
          {record.status === 'running' && (
            <Button
              type="text"
              icon={<PauseCircleOutlined />}
              onClick={() => handlePause(record.id)}
              style={{ color: '#faad14' }}
            />
          )}
          <Popconfirm
            title="Delete this task?"
            description="This will delete all associated caption results."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const runningCount = tasks.filter(t => t.status === 'running').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <RobotOutlined style={{ marginRight: '12px', color: '#6366f1' }} />
          Captioning Tasks
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchTasks}>
            Refresh
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => navigate('/captioning/create')}
          >
            Create Task
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic title="Total Tasks" value={tasks.length} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Running" value={runningCount} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Completed" value={completedCount} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default CaptioningTaskList;
